import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  FileText,
  Download,
  CalendarClock,
  Award,
  UploadCloud,
  User as UserIcon,
  Building2,
  Filter,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Infinity as InfinityIcon,
  Clock,
  FileDown,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SOURCE_META = {
  manual: { label: "Manuale", icon: UploadCloud, cls: "bg-slate-100 text-slate-700 border-slate-200" },
  deadline_attachment: { label: "Da scadenza", icon: CalendarClock, cls: "bg-sky-100 text-sky-800 border-sky-200" },
  attestati_import: { label: "Da attestato", icon: Award, cls: "bg-amber-100 text-amber-800 border-amber-200" },
};

function SourceBadge({ source }) {
  const meta = SOURCE_META[source] || SOURCE_META.manual;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${meta.cls}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function WorkerDocumentsPage() {
  const { id } = useParams();
  const [worker, setWorker] = useState(null);
  const [company, setCompany] = useState(null);
  const [docs, setDocs] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [filterCategory, setFilterCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const w = await api.get(`/workers/${id}`);
        if (cancelled) return;
        setWorker(w.data);
        if (w.data?.company_id) {
          try {
            const c = await api.get(`/companies/${w.data.company_id}`);
            if (!cancelled) setCompany(c.data);
          } catch (err) {
            // Non-fatal: company fetch may fail (deleted or permission denied).
            // Worker view can still load without company details.
            console.debug("[WorkerDocuments] company fetch failed:", err?.message || err);
          }
        }
        const [d, dl] = await Promise.all([
          api.get(`/documents?worker_id=${id}`),
          api.get(`/deadlines?worker_id=${id}`),
        ]);
        if (!cancelled) {
          setDocs(d.data);
          setDeadlines(dl.data);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Errore caricamento");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const categories = useMemo(() => {
    const s = new Set();
    docs.forEach((d) => s.add(d.category || "generale"));
    return Array.from(s).sort();
  }, [docs]);

  // Group by category, then sort by created_at desc within each group
  const grouped = useMemo(() => {
    const filtered = filterCategory === "all" ? docs : docs.filter((d) => (d.category || "generale") === filterCategory);
    const map = new Map();
    filtered.forEach((d) => {
      const k = d.category || "generale";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(d);
    });
    for (const arr of map.values()) {
      arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [docs, filterCategory]);

  const stats = useMemo(() => {
    const byCat = {};
    docs.forEach((d) => {
      const k = d.category || "generale";
      byCat[k] = (byCat[k] || 0) + 1;
    });
    return { total: docs.length, byCat };
  }, [docs]);

  const deadlineStats = useMemo(() => {
    const s = { total: deadlines.length, ok: 0, warning: 0, expired: 0, no_term: 0, completed: 0 };
    deadlines.forEach((d) => {
      const st = d.status;
      if (st === "expired") s.expired++;
      else if (st === "warning") s.warning++;
      else if (st === "no_term") s.no_term++;
      else if (st === "completed") s.completed++;
      else s.ok++;
    });
    return s;
  }, [deadlines]);

  // Ordina scadenze: prima scadute, poi warning, poi ok/no_term, poi completate
  const orderedDeadlines = useMemo(() => {
    const rank = { expired: 0, warning: 1, ok: 2, no_term: 3, completed: 4 };
    return [...deadlines].sort((a, b) => {
      const ra = rank[a.status] ?? 5;
      const rb = rank[b.status] ?? 5;
      if (ra !== rb) return ra - rb;
      return (a.due_date || "9999-12-31").localeCompare(b.due_date || "9999-12-31");
    });
  }, [deadlines]);

  const workerName = worker ? `${worker.first_name || ""} ${worker.last_name || ""}`.trim() : "";

  return (
    <div className="space-y-6" data-testid="worker-documents-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/workers">
          <Button variant="outline" className="rounded-full h-9" data-testid="worker-docs-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Lavoratori
          </Button>
        </Link>
        {worker && (
          <Button
            variant="outline"
            className="rounded-full h-9 btn-lift"
            onClick={async () => {
              try {
                const r = await api.get(`/workers/${id}/export-pdf`, { responseType: "blob" });
                const blob = new Blob([r.data], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                const safe = (workerName || "lavoratore").replace(/\s+/g, "_");
                a.download = `scheda_${safe}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success("PDF generato");
              } catch (err) {
                toast.error(err.response?.data?.detail || "Errore generazione PDF");
              }
            }}
            data-testid="worker-export-pdf"
            title="Scarica scheda ufficiale in PDF con anagrafica, scadenze e attestati"
          >
            <FileDown className="w-4 h-4 mr-1.5" /> Esporta scheda PDF
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-muted-foreground">Caricamento…</p>
      ) : !worker ? (
        <p className="text-muted-foreground">Lavoratore non trovato.</p>
      ) : (
        <>
          <div className="bg-card border border-border rounded-lg p-5 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                <UserIcon className="w-3.5 h-3.5" /> Lavoratore
              </div>
              <h1 className="text-2xl font-bold mt-1">{workerName || "—"}</h1>
              <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                {worker.tax_code && <span>CF: <code>{worker.tax_code}</code></span>}
                {worker.role && <span>· {worker.role}</span>}
                {worker.department && <span>· {worker.department}</span>}
                {company && (
                  <span className="inline-flex items-center gap-1">
                    · <Building2 className="w-3 h-3" />{" "}
                    <Link to={`/companies/${company.id}`} className="text-primary hover:underline">
                      {company.name}
                    </Link>
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Totale documenti</div>
              <div className="text-3xl font-bold mt-1">{stats.total}</div>
            </div>
          </div>

          {/* Riepilogo scadenze */}
          <div className="bg-card border border-border rounded-lg p-5" data-testid="worker-deadlines-summary">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" /> Scadenze del lavoratore
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Panoramica delle scadenze aperte + storico completato
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {deadlineStats.expired > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-rose-100 text-rose-800 border border-rose-200">
                    <XCircle className="w-3 h-3" /> Scadute: {deadlineStats.expired}
                  </span>
                )}
                {deadlineStats.warning > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                    <AlertTriangle className="w-3 h-3" /> In scadenza: {deadlineStats.warning}
                  </span>
                )}
                {deadlineStats.ok > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                    <CheckCircle2 className="w-3 h-3" /> Attive: {deadlineStats.ok}
                  </span>
                )}
                {deadlineStats.no_term > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-sky-100 text-sky-800 border border-sky-200">
                    <InfinityIcon className="w-3 h-3" /> Senza termine: {deadlineStats.no_term}
                  </span>
                )}
                {deadlineStats.completed > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                    <CheckCircle2 className="w-3 h-3" /> Completate: {deadlineStats.completed}
                  </span>
                )}
              </div>
            </div>
            {orderedDeadlines.length === 0 ? (
              <div className="text-sm text-muted-foreground italic text-center py-4">
                Nessuna scadenza attiva per questo lavoratore.
              </div>
            ) : (
              <div className="divide-y divide-border border border-border rounded-md overflow-hidden">
                {orderedDeadlines.map((d) => {
                  const meta = {
                    expired: { icon: XCircle, cls: "text-rose-700", pill: "bg-rose-100 text-rose-800 border-rose-200", label: "Scaduta" },
                    warning: { icon: AlertTriangle, cls: "text-amber-700", pill: "bg-amber-100 text-amber-800 border-amber-200", label: "In scadenza" },
                    ok: { icon: CheckCircle2, cls: "text-emerald-700", pill: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Attiva" },
                    no_term: { icon: InfinityIcon, cls: "text-sky-700", pill: "bg-sky-100 text-sky-800 border-sky-200", label: "Senza termine" },
                    completed: { icon: CheckCircle2, cls: "text-slate-500", pill: "bg-slate-100 text-slate-700 border-slate-200", label: "Completata" },
                  }[d.status] || { icon: CheckCircle2, cls: "text-slate-500", pill: "bg-slate-100 text-slate-700 border-slate-200", label: d.status };
                  const Icon = meta.icon;
                  return (
                    <div key={d.id} className="px-4 py-3 flex items-center gap-3 text-sm" data-testid={`worker-deadline-${d.id}`}>
                      <Icon className={`w-4 h-4 shrink-0 ${meta.cls}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{d.title}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {d.subtype_label || d.subtype || d.category}
                          {d.due_date && (
                            <>
                              {" · Scadenza: "}
                              <b>{fmtDate(d.due_date)}</b>
                            </>
                          )}
                          {d.completed_date && (
                            <> · Completata il <b>{fmtDate(d.completed_date)}</b></>
                          )}
                        </div>
                      </div>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${meta.pill}`}>
                        {meta.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold">Archivio storico</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Tutti gli attestati e documenti collegati a questo lavoratore, in ordine cronologico (dal più
                recente).
              </p>
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-56 h-10" data-testid="worker-docs-category-filter">
                <Filter className="w-3.5 h-3.5 mr-2 opacity-60" />
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c} ({stats.byCat[c]})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {docs.length === 0 ? (
            <div className="bg-card border border-dashed border-border rounded-lg p-10 text-center text-muted-foreground italic">
              Nessun documento collegato a questo lavoratore. I nuovi allegati caricati sulle sue scadenze
              e gli attestati importati compariranno qui in automatico.
            </div>
          ) : (
            <div className="space-y-6">
              {grouped.map(([cat, items]) => (
                <div key={cat} className="bg-card border border-border rounded-lg overflow-hidden">
                  <div className="px-5 py-3 bg-muted/40 flex items-center justify-between">
                    <div className="font-semibold uppercase tracking-widest text-xs text-muted-foreground">
                      {cat}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {items.length} document{items.length === 1 ? "o" : "i"}
                    </span>
                  </div>
                  <div className="divide-y divide-border">
                    {items.map((d, idx) => (
                      <div key={d.id} className="px-5 py-4 flex items-start gap-4">
                        {/* Timeline dot */}
                        <div className="pt-1 flex flex-col items-center shrink-0">
                          <div
                            className={`w-2.5 h-2.5 rounded-full ${
                              idx === 0 ? "bg-emerald-500" : "bg-slate-300"
                            } ring-4 ring-offset-0 ${idx === 0 ? "ring-emerald-100" : "ring-slate-100"}`}
                          />
                          {idx < items.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-2 min-h-[24px]" />}
                        </div>
                        <FileText className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium flex items-center gap-2 flex-wrap">
                            <span className="truncate">{d.name}</span>
                            <SourceBadge source={d.source || "manual"} />
                            {d.version > 1 && (
                              <span className="text-[10px] uppercase tracking-widest bg-indigo-100 text-indigo-700 border border-indigo-200 rounded px-1.5 py-0.5">
                                v{d.version}
                              </span>
                            )}
                            {idx === 0 && (
                              <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
                                Più recente
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1">
                              <CalendarClock className="w-3 h-3" /> {fmtDate(d.created_at)}
                            </span>
                            {d.filename && (
                              <>
                                <span>·</span>
                                <span className="truncate max-w-[280px]">{d.filename}</span>
                              </>
                            )}
                            {d.size ? (
                              <>
                                <span>·</span>
                                <span>{(d.size / 1024).toFixed(1)} KB</span>
                              </>
                            ) : null}
                          </div>
                        </div>
                        <a
                          href={`${BACKEND}${d.file_url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs uppercase tracking-widest text-primary inline-flex items-center gap-1 btn-lift shrink-0"
                          data-testid={`worker-docs-download-${d.id}`}
                        >
                          <Download className="w-3.5 h-3.5" /> Scarica
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
